import {
  ApprovalRequestId,
  isToolLifecycleItemType,
  type OrchestrationLatestTurn,
  type OrchestrationThreadActivity,
  type OrchestrationProposedPlanId,
  type ProviderKind,
  type ToolLifecycleItemType,
  type UserInputQuestion,
  type ThreadId,
  type TurnId,
} from "@taskin-code/contracts";
import {
  collectSubagentProviderThreadIds,
  decodeSubagentAgentStates,
  decodeSubagentReceiverAgents,
} from "@taskin-code/shared/subagents";
import {
  directDelegations,
  hasUnreadDelegateReport,
  isDelegateWorking,
  isLiveDelegateThread,
} from "@taskin-code/shared/delegation";
import { isPendingRequestAnswerable } from "@taskin-code/shared/threadSummary";
import { deriveReadableToolTitle, normalizeCompactToolLabel } from "./lib/toolCallLabel";

import type {
  ChatMessage,
  ProposedPlan,
  SessionPhase,
  Thread,
  ThreadSession,
  TurnDiffSummary,
} from "./types";

export type ProviderPickerKind = ProviderKind;

export const PROVIDER_OPTIONS: Array<{
  value: ProviderPickerKind;
  label: string;
  available: boolean;
}> = [
  { value: "codex", label: "Codex", available: true },
  { value: "claudeAgent", label: "Claude", available: true },
];

export interface WorkLogEntry {
  id: string;
  createdAt: string;
  label: string;
  detail?: string;
  command?: string;
  /** Real tool output, already truncated server-side (see `toolActivityDetails.ts`). */
  output?: string;
  outputTruncated?: boolean;
  exitCode?: number;
  isError?: boolean;
  changedFiles?: ReadonlyArray<string>;
  tone: "thinking" | "tool" | "info" | "error";
  /**
   * The turn that produced this row. Two things need it: lifecycle collapsing,
   * which must not merge identical tool calls made in different turns, and the
   * transcript's turn folds, which hide a settled turn's work behind a single
   * "Trabajado por ..." row.
   */
  turnId?: TurnId;
  /**
   * Marks a row that describes the conversation rather than the running turn
   * (`/clear`, a manual `/compact`). Such a row outlives the turn it was
   * derived next to, so it must never be mistaken for current progress.
   */
  isBoundary?: boolean;
  /**
   * Narrows which boundary this row is, since `activityKind` itself does not
   * survive `deriveWorkLogEntries` (stripped in its final `.map()`). The
   * timeline needs this to pick the right icon without matching on `label`
   * text, which varies with compaction status.
   */
  boundaryKind?: "clear" | "compaction";
  /**
   * Marks a `taskin_memory` row, for the same reason `boundaryKind` exists:
   * `activityKind` does not survive `deriveWorkLogEntries`, and the timeline
   * must not pick an icon by matching on label text.
   *
   * Worth its own glyph rather than the generic tool one because these two rows
   * answer a question nothing else in the transcript does — *why* the agent
   * went straight to the right procedure instead of improvising, and what it
   * wants the next thread to know.
   */
  memoryKind?: "recalled" | "proposed";
  /**
   * The compaction this row reports, when it reports one.
   *
   * Structured rather than baked into the label so the row can state what was
   * saved without anyone parsing text back out, and so the timeline can find
   * the cut-off (a *completed* compaction) without inspecting raw payloads.
   */
  compaction?: WorkLogCompaction;
  toolTitle?: string;
  itemType?: ToolLifecycleItemType;
  requestKind?: PendingApproval["requestKind"];
  /**
   * The question this row announced, when it announced one.
   *
   * Carried through so the timeline can offer to reopen a question the user
   * folded away: the row is the only place it is still visible, and it is where
   * someone looks to find out what the agent is waiting for.
   */
  userInputRequestId?: ApprovalRequestId;
  /**
   * The questions the agent asked here, together with what was answered.
   *
   * Present on the row that announced the question, so the transcript states
   * the decision where it was taken instead of leaving behind two opaque rows
   * ("User input requested" / "User input submitted") that hide both the
   * question and the answer inside their raw payloads.
   */
  userInput?: WorkLogUserInput;
  subagentTask?: WorkLogSubagentTask;
  /**
   * Set when this row absorbed one or more identical consecutive rows during
   * collapsing (e.g. repeated `runtime.warning` activities such as
   * "Reconnecting... N/5"). Undefined/1 means the row was not collapsed.
   */
  count?: number;
}

/**
 * One question of an `AskUserQuestion`-style prompt, paired with the answer the
 * user gave it.
 *
 * Both halves live on one structure because the transcript reads them together:
 * a question without its answer says nothing about the decision, and an answer
 * without its question is a bare label ("date-fns") nobody can place.
 */
export interface WorkLogUserInputQuestion {
  id: string;
  header: string;
  question: string;
  options: ReadonlyArray<{ label: string; description: string }>;
  multiSelect: boolean;
  /** Offered options the user picked, in the order they were submitted. */
  selectedLabels: ReadonlyArray<string>;
  /** What the user typed instead of picking an option, when they did. */
  customAnswer?: string;
}

/**
 * A question the agent asked the user, as the transcript shows it.
 *
 * `status` is what tells the three outcomes apart: `pending` is still blocking
 * the agent (the row offers to reopen it), `answered` carries the decision, and
 * `cancelled` is a question that was retracted — an interrupted turn resolves
 * the request with no answers at all, and saying so beats a row that looks
 * answered with every question blank.
 */
export interface WorkLogUserInput {
  requestId: ApprovalRequestId;
  status: "pending" | "answered" | "cancelled";
  questions: ReadonlyArray<WorkLogUserInputQuestion>;
}

/**
 * What the server recorded about one compaction, read off the activity payload
 * (`ContextCompactionActivityPayload`).
 *
 * `status` is the load-bearing field: only a `completed` compaction actually
 * replaced the context, which is what makes it a cut-off point for the
 * transcript. An in-flight one is still showing the history it may yet compact,
 * and a failed one left the conversation untouched.
 */
export interface WorkLogCompaction {
  status: "inProgress" | "completed" | "failed";
  trigger?: "manual" | "auto";
  /** Context size before compacting, when the provider reported it. */
  preTokens?: number;
  /** Context size afterwards — the provider's number, or the first snapshot after it. */
  postTokens?: number;
  /** The recap the agent keeps in place of the compacted history. */
  summaryText?: string;
}

/**
 * A subagent that runs *inside* the same session — Claude's `Task` tool. There
 * is no child thread to open, so this row is the only place its prompt, its live
 * action and its final report are ever shown.
 *
 */
export interface WorkLogSubagentTask {
  /**
   * `in-session` is Claude's `Task`: it runs inside this thread, so its prompt
   * and report live on this row. `child-thread` is Codex: each subagent gets its
   * own thread, so the row links there instead of duplicating the transcript.
   */
  kind: "in-session" | "child-thread";
  subagentType?: string | undefined;
  description?: string | undefined;
  prompt?: string | undefined;
  /** The tool the subagent is running right now; absent once it finished. */
  currentAction?: string | undefined;
  status: "running" | "completed" | "failed";
  /**
   * `in-session` only: the `tool_use` id of the `Task` call. It identifies the
   * call across its updates and names the child thread that mirrors its
   * transcript (`subagent:<parentThreadId>:<taskToolUseId>`).
   */
  taskToolUseId?: string | undefined;
  /** `child-thread` only: the provider thread ids of the spawned subagents. */
  receiverProviderThreadIds?: ReadonlyArray<string> | undefined;
  /** `child-thread` only: names for the receivers that reported any. */
  receivers?: ReadonlyArray<WorkLogSubagentTaskReceiver> | undefined;
  /**
   * Openable local threads, resolved against the loaded thread list by
   * `enrichSubagentWorkEntries` — present only for subagent threads that really
   * exist, so the row never offers a link that goes nowhere.
   */
  threadLinks?: ReadonlyArray<WorkLogSubagentTaskThreadLink> | undefined;
}

export interface WorkLogSubagentTaskReceiver {
  providerThreadId: string;
  nickname?: string | undefined;
  role?: string | undefined;
  model?: string | undefined;
  status?: string | undefined;
}

export interface WorkLogSubagentTaskThreadLink {
  threadId: string;
  label: string;
}

interface DerivedWorkLogEntry extends WorkLogEntry {
  activityKind: OrchestrationThreadActivity["kind"];
  collapseKey?: string;
  collapseCommand?: string;
  toolName?: string;
}

export interface PendingApproval {
  requestId: ApprovalRequestId;
  requestKind: "command" | "file-read" | "file-change";
  createdAt: string;
  detail?: string;
}

export interface PendingUserInput {
  requestId: ApprovalRequestId;
  createdAt: string;
  questions: ReadonlyArray<UserInputQuestion>;
}

export interface ActivePlanState {
  createdAt: string;
  turnId: TurnId | null;
  explanation?: string | null;
  steps: Array<{
    step: string;
    status: "pending" | "inProgress" | "completed";
  }>;
}

export interface ActiveBackgroundTasksState {
  activeCount: number;
}

/**
 * A subagent still working for this thread.
 *
 * `key` identifies the row for React lists — for the in-session and collab
 * shapes it is derived from the subagent's identity, not a stable provider id,
 * since neither adapter's payload carries one (see
 * `deriveActiveSubagentTasksState`).
 *
 * `threadId` is only set for a delegation that owns a real thread of its own
 * (managed agents), and it is what makes the entry navigable. The in-session
 * shapes have no thread to open.
 */
export interface ActiveSubagentTask {
  key: string;
  subagentType?: string | undefined;
  description?: string | undefined;
  threadId?: ThreadId | undefined;
  /**
   * When the delegation's current turn started, so its row can show how long it
   * has been at it. Only delegations have one: the in-session shapes are
   * reconstructed from activities, which carry no turn of their own.
   */
  startedAt?: string | undefined;
  /**
   * Set on a delegation that has already answered and is waiting for its parent
   * to read it. Such a row is not work in flight, and nothing may count it as
   * such (see `deriveDelegationsAwaitingParent`).
   */
  awaitingParent?: boolean | undefined;
}

export interface ActiveSubagentTasksState {
  tasks: ReadonlyArray<ActiveSubagentTask>;
}

export interface LatestProposedPlanState {
  id: OrchestrationProposedPlanId;
  createdAt: string;
  updatedAt: string;
  turnId: TurnId | null;
  planMarkdown: string;
  implementedAt: string | null;
  implementationThreadId: ThreadId | null;
}

export type TimelineEntry =
  | {
      id: string;
      kind: "message";
      createdAt: string;
      message: ChatMessage;
    }
  | {
      id: string;
      kind: "proposed-plan";
      createdAt: string;
      proposedPlan: ProposedPlan;
    }
  | {
      id: string;
      kind: "work";
      createdAt: string;
      entry: WorkLogEntry;
    };

export function formatDuration(durationMs: number): string {
  if (!Number.isFinite(durationMs) || durationMs < 0) return "0ms";
  if (durationMs < 1_000) return `${Math.max(1, Math.round(durationMs))}ms`;
  if (durationMs < 10_000) return `${(durationMs / 1_000).toFixed(1)}s`;
  if (durationMs < 60_000) return `${Math.round(durationMs / 1_000)}s`;
  const minutes = Math.floor(durationMs / 60_000);
  const seconds = Math.round((durationMs % 60_000) / 1_000);
  if (seconds === 0) return `${minutes}m`;
  if (seconds === 60) return `${minutes + 1}m`;
  return `${minutes}m ${seconds}s`;
}

export function formatElapsed(startIso: string, endIso: string | undefined): string | null {
  if (!endIso) return null;
  const startedAt = Date.parse(startIso);
  const endedAt = Date.parse(endIso);
  if (Number.isNaN(startedAt) || Number.isNaN(endedAt) || endedAt < startedAt) {
    return null;
  }
  return formatDuration(endedAt - startedAt);
}

type LatestTurnTiming = Pick<
  OrchestrationLatestTurn,
  "turnId" | "state" | "startedAt" | "completedAt"
>;
type SessionActivityState = Pick<ThreadSession, "orchestrationStatus" | "activeTurnId">;

export function isLatestTurnSettled(
  latestTurn: LatestTurnTiming | null,
  session: SessionActivityState | null,
): boolean {
  if (!latestTurn?.startedAt) return false;
  if (!latestTurn.completedAt) return false;
  if (latestTurn.state === "interrupted" || latestTurn.state === "error") {
    return true;
  }
  if (!session) return true;
  if (session.orchestrationStatus === "running") return false;
  return true;
}

export function hasLiveLatestTurn(
  latestTurn: LatestTurnTiming | null,
  session: SessionActivityState | null,
): boolean {
  if (!latestTurn?.startedAt) {
    return false;
  }
  return !isLatestTurnSettled(latestTurn, session);
}

export function deriveActiveWorkStartedAt(
  latestTurn: LatestTurnTiming | null,
  session: SessionActivityState | null,
  sendStartedAt: string | null,
): string | null {
  const runningTurnId =
    session?.orchestrationStatus === "running" ? (session.activeTurnId ?? null) : null;
  if (runningTurnId !== null && runningTurnId === latestTurn?.turnId) {
    return latestTurn?.startedAt ?? sendStartedAt;
  }
  if (runningTurnId !== null) {
    return sendStartedAt;
  }
  if (!isLatestTurnSettled(latestTurn, session)) {
    return latestTurn?.startedAt ?? sendStartedAt;
  }
  return sendStartedAt;
}

function requestKindFromRequestType(requestType: unknown): PendingApproval["requestKind"] | null {
  switch (requestType) {
    case "command_execution_approval":
    case "exec_command_approval":
      return "command";
    case "file_read_approval":
      return "file-read";
    case "file_change_approval":
    case "apply_patch_approval":
      return "file-change";
    default:
      return null;
  }
}

function isStalePendingRequestFailureDetail(detail: string | undefined): boolean {
  const normalized = detail?.toLowerCase();
  if (!normalized) {
    return false;
  }
  return (
    normalized.includes("stale pending approval request") ||
    normalized.includes("stale pending user-input request") ||
    normalized.includes("unknown pending approval request") ||
    normalized.includes("unknown pending permission request") ||
    normalized.includes("unknown pending user-input request")
  );
}

/**
 * The approvals and questions this thread still owes an answer to.
 *
 * `latestTurn` is what separates those from the leftovers of a turn that already
 * ended: the provider stopped listening when the turn did, so a prompt from a
 * dead turn cannot be answered — it would come back "stale pending request" —
 * and showing it would be offering a control that does nothing. Same reading the
 * server gives these requests in `deriveThreadSummaryMetadata`, deliberately:
 * when the two disagreed, the server quietly refused to resume delegating agents
 * over questions the UI had long stopped showing.
 */
export function derivePendingApprovals(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
  latestTurn: Pick<OrchestrationLatestTurn, "turnId" | "completedAt"> | null,
): PendingApproval[] {
  const openByRequestId = new Map<ApprovalRequestId, PendingApproval>();
  const ordered = [...activities].toSorted(compareActivitiesByOrder);

  for (const activity of ordered) {
    const payload =
      activity.payload && typeof activity.payload === "object"
        ? (activity.payload as Record<string, unknown>)
        : null;
    const requestId =
      payload && typeof payload.requestId === "string"
        ? ApprovalRequestId.makeUnsafe(payload.requestId)
        : null;
    const requestKind =
      payload &&
      (payload.requestKind === "command" ||
        payload.requestKind === "file-read" ||
        payload.requestKind === "file-change")
        ? payload.requestKind
        : payload
          ? requestKindFromRequestType(payload.requestType)
          : null;
    const detail = payload && typeof payload.detail === "string" ? payload.detail : undefined;

    if (activity.kind === "approval.requested" && requestId && requestKind) {
      if (!isPendingRequestAnswerable(activity.turnId, latestTurn)) {
        continue;
      }
      openByRequestId.set(requestId, {
        requestId,
        requestKind,
        createdAt: activity.createdAt,
        ...(detail ? { detail } : {}),
      });
      continue;
    }

    if (activity.kind === "approval.resolved" && requestId) {
      openByRequestId.delete(requestId);
      continue;
    }

    if (
      activity.kind === "provider.approval.respond.failed" &&
      requestId &&
      isStalePendingRequestFailureDetail(detail)
    ) {
      openByRequestId.delete(requestId);
      continue;
    }
  }

  return [...openByRequestId.values()].toSorted((left, right) =>
    left.createdAt.localeCompare(right.createdAt),
  );
}

function parseUserInputQuestions(
  payload: Record<string, unknown> | null,
): ReadonlyArray<UserInputQuestion> | null {
  const questions = payload?.questions;
  if (!Array.isArray(questions)) {
    return null;
  }
  const parsed = questions
    .map<UserInputQuestion | null>((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const question = entry as Record<string, unknown>;
      if (
        typeof question.id !== "string" ||
        typeof question.header !== "string" ||
        typeof question.question !== "string" ||
        !Array.isArray(question.options)
      ) {
        return null;
      }
      const options = question.options
        .map<UserInputQuestion["options"][number] | null>((option) => {
          if (!option || typeof option !== "object") return null;
          const optionRecord = option as Record<string, unknown>;
          if (
            typeof optionRecord.label !== "string" ||
            typeof optionRecord.description !== "string"
          ) {
            return null;
          }
          return {
            label: optionRecord.label,
            description: optionRecord.description,
          };
        })
        .filter((option): option is UserInputQuestion["options"][number] => option !== null);
      if (options.length === 0) {
        return null;
      }
      return {
        id: question.id,
        header: question.header,
        question: question.question,
        options,
        // Always spelled out, matching the schema's own default: the composer
        // reads it to decide whether a question takes one answer or several,
        // and the transcript to describe the answer it got.
        multiSelect: typeof question.multiSelect === "boolean" ? question.multiSelect : false,
      };
    })
    .filter((question): question is UserInputQuestion => question !== null);
  return parsed.length > 0 ? parsed : null;
}

/**
 * Whether a question is still worth putting in front of the user.
 *
 * Deliberately looser than [[isPendingRequestAnswerable]], which additionally
 * requires the turn to still be running. A provider stops listening for an
 * *approval* when its turn ends, but a question is a blocked JSON-RPC call:
 * Codex keeps it in `pendingUserInputs` until the whole session dies, not until
 * the turn does. Hiding it the moment the turn closed is what made a question
 * vanish for good, with the agent still parked on it and no way back.
 *
 * The turn identity check stays, so a question from an earlier turn does not
 * resurface once the conversation has moved past it.
 *
 * This does NOT belong in the shared helper: `threadSummary` feeds
 * `hasPendingUserInput`, and `decideDelegationWake` reads that as "the user is
 * the one being waited on". Counting a dead question as open there is precisely
 * what once left delegating agents asleep forever. Being generous is safe here —
 * the worst case is offering to answer something nobody is listening to, and the
 * `provider.user-input.respond.failed` branch below clears it on the first try.
 */
function isPendingUserInputAnswerable(
  requestTurnId: string | null | undefined,
  latestTurn: Pick<OrchestrationLatestTurn, "turnId" | "completedAt"> | null,
): boolean {
  if (!latestTurn) {
    return false;
  }
  return requestTurnId == null || requestTurnId === latestTurn.turnId;
}

/** The open questions of this thread. See [[derivePendingApprovals]]. */
export function derivePendingUserInputs(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
  latestTurn: Pick<OrchestrationLatestTurn, "turnId" | "completedAt"> | null,
): PendingUserInput[] {
  const openByRequestId = new Map<ApprovalRequestId, PendingUserInput>();
  const ordered = [...activities].toSorted(compareActivitiesByOrder);

  for (const activity of ordered) {
    const payload =
      activity.payload && typeof activity.payload === "object"
        ? (activity.payload as Record<string, unknown>)
        : null;
    const requestId =
      payload && typeof payload.requestId === "string"
        ? ApprovalRequestId.makeUnsafe(payload.requestId)
        : null;
    const detail = payload && typeof payload.detail === "string" ? payload.detail : undefined;

    if (activity.kind === "user-input.requested" && requestId) {
      const questions = parseUserInputQuestions(payload);
      if (!questions) {
        continue;
      }
      if (!isPendingUserInputAnswerable(activity.turnId, latestTurn)) {
        continue;
      }
      openByRequestId.set(requestId, {
        requestId,
        createdAt: activity.createdAt,
        questions,
      });
      continue;
    }

    if (activity.kind === "user-input.resolved" && requestId) {
      openByRequestId.delete(requestId);
      continue;
    }

    if (
      activity.kind === "provider.user-input.respond.failed" &&
      requestId &&
      isStalePendingRequestFailureDetail(detail)
    ) {
      openByRequestId.delete(requestId);
    }
  }

  return [...openByRequestId.values()].toSorted((left, right) =>
    left.createdAt.localeCompare(right.createdAt),
  );
}

export function deriveActivePlanState(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
  latestTurnId: TurnId | undefined,
): ActivePlanState | null {
  // The plan is strictly scoped to the active turn: only `turn.plan.updated`
  // activities emitted by `latestTurnId` count. A follow-up turn that emits no
  // plan update shows no plan at all, instead of resurrecting a previous turn's
  // stale task list.
  if (!latestTurnId) {
    return null;
  }
  const ordered = [...activities].toSorted(compareActivitiesByOrder);
  const latest =
    ordered.findLast(
      (activity) => activity.kind === "turn.plan.updated" && activity.turnId === latestTurnId,
    ) ?? null;
  if (!latest) {
    return null;
  }
  const payload =
    latest.payload && typeof latest.payload === "object"
      ? (latest.payload as Record<string, unknown>)
      : null;
  const rawPlan = payload?.plan;
  if (!Array.isArray(rawPlan)) {
    return null;
  }
  const steps = rawPlan
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const record = entry as Record<string, unknown>;
      if (typeof record.step !== "string") {
        return null;
      }
      const status =
        record.status === "completed" || record.status === "inProgress" ? record.status : "pending";
      return {
        step: record.step,
        status,
      };
    })
    .filter(
      (
        step,
      ): step is {
        step: string;
        status: "pending" | "inProgress" | "completed";
      } => step !== null,
    );
  if (steps.length === 0) {
    return null;
  }
  return {
    createdAt: latest.createdAt,
    turnId: latest.turnId,
    ...(payload && "explanation" in payload
      ? { explanation: payload.explanation as string | null }
      : {}),
    steps,
  };
}

// Counts still-running background work for the active turn so compact UI can surface agent activity.
export function deriveActiveBackgroundTasksState(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
  latestTurnId: TurnId | undefined,
): ActiveBackgroundTasksState | null {
  const ordered = [...activities].toSorted(compareActivitiesByOrder);
  const activeTasks = new Map<string, { taskType?: string | undefined }>();

  for (const activity of ordered) {
    if (
      latestTurnId &&
      activity.turnId &&
      activity.turnId !== latestTurnId &&
      activity.kind !== "task.completed"
    ) {
      continue;
    }

    if (
      activity.kind !== "task.started" &&
      activity.kind !== "task.progress" &&
      activity.kind !== "task.completed"
    ) {
      continue;
    }

    const payload =
      activity.payload && typeof activity.payload === "object"
        ? (activity.payload as Record<string, unknown>)
        : null;
    const taskId = payload && typeof payload.taskId === "string" ? payload.taskId : null;
    if (!taskId) {
      continue;
    }

    if (activity.kind === "task.completed") {
      activeTasks.delete(taskId);
      continue;
    }

    const previous = activeTasks.get(taskId);
    const taskType = payload && typeof payload.taskType === "string" ? payload.taskType : undefined;
    activeTasks.set(taskId, {
      taskType: taskType ?? previous?.taskType,
    });
  }

  const activeCount = [...activeTasks.values()].filter((task) => task.taskType !== "plan").length;
  return activeCount > 0 ? { activeCount } : null;
}

/**
 * Tracks subagents (`collab_agent_tool_call`) still running in the active turn,
 * so a fixed indicator above the composer can surface them without requiring the
 * transcript to be scrolled into view.
 *
 * The unit differs per provider, and so does the key:
 *
 * - Codex spawns one thread per subagent and names them, so each receiver id is
 *   an entry with a genuinely stable key. A call that has not named its receivers
 *   yet contributes nothing — there is no subagent to announce yet.
 * - Claude's payload carries no id at all (see `extractSubagentTask`), so calls
 *   are keyed by identity (`subagentType` + `description` + `prompt`). Two
 *   concurrent subagents sharing that identity collapse into one entry, the same
 *   trade-off the work log makes.
 */
export function deriveActiveSubagentTasksState(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
  latestTurnId: TurnId | undefined,
): ActiveSubagentTasksState | null {
  const ordered = [...activities].toSorted(compareActivitiesByOrder);
  const runningByKey = new Map<string, ActiveSubagentTask>();
  const openCalls: OpenSubagentCall[] = [];
  // Both providers emit collab activities with no turn attached, interleaved with
  // the turn's own ones, so dropping every untagged activity would hide the
  // indicator during the very turn it describes. Where they sit is what tells
  // them apart: the first activity attributed to the active turn is the border.
  // Before it lie the leftovers of previous turns — the ones that used to
  // resurrect finished subagents on every new turn; after it, the live ones.
  const activeTurnStartIndex = latestTurnId
    ? ordered.findIndex((activity) => activity.turnId === latestTurnId)
    : -1;

  for (const [index, activity] of ordered.entries()) {
    if (latestTurnId) {
      if (activity.turnId) {
        if (activity.turnId !== latestTurnId) {
          continue;
        }
        // An untagged activity ahead of the border, or with no border at all
        // (nothing of this turn recorded yet), is only trusted once the turn has
        // shown up: it costs at most one update of delay before it counts.
      } else if (activeTurnStartIndex < 0 || index < activeTurnStartIndex) {
        continue;
      }
    }
    if (activity.kind !== "tool.updated" && activity.kind !== "tool.completed") {
      continue;
    }
    const payload =
      activity.payload && typeof activity.payload === "object"
        ? (activity.payload as Record<string, unknown>)
        : null;
    const subagentTask = extractSubagentTask(activity, payload);
    if (!subagentTask) {
      continue;
    }
    const entries = activeSubagentTaskEntries(subagentTask);

    if (subagentTask.status === "running") {
      if (entries.length === 0) {
        continue;
      }
      for (const task of entries) {
        runningByKey.set(task.key, task);
      }
      rememberOpenSubagentCall(
        openCalls,
        subagentTask.kind,
        entries.map((task) => task.key),
      );
      continue;
    }

    for (const key of closeSubagentCall(
      openCalls,
      subagentTask.kind,
      entries.map((task) => task.key),
    )) {
      runningByKey.delete(key);
    }
  }

  const tasks = [...runningByKey.values()];
  return tasks.length > 0 ? { tasks } : null;
}

/**
 * The entries one still-running subagent call has announced, oldest call first.
 *
 * Activities persist no item id (issue #40), so a call is recognized by the keys
 * it announced: a later update of the same Codex call repeats its receivers, and
 * a Claude `Task` repeats its `tool_use` id. That is the only identity available
 * on this side, and it is what lets a terminal event close what its own call
 * opened.
 */
interface OpenSubagentCall {
  readonly kind: WorkLogSubagentTask["kind"];
  keys: ReadonlyArray<string>;
}

function rememberOpenSubagentCall(
  openCalls: OpenSubagentCall[],
  kind: WorkLogSubagentTask["kind"],
  keys: ReadonlyArray<string>,
): void {
  // A Codex call names its receivers as it spawns them, so an update that adds a
  // second one belongs to the call that already announced the first.
  const existing = openCalls.find(
    (call) => call.kind === kind && call.keys.some((key) => keys.includes(key)),
  );
  if (existing) {
    existing.keys = [...new Set([...existing.keys, ...keys])];
    return;
  }
  openCalls.push({ kind, keys: [...keys] });
}

/**
 * Resolves which keys a terminal event closes.
 *
 * The event that ends a call does not have to repeat its receivers — a Codex
 * `tool.completed` routinely carries none — and taking it literally left the
 * indicator pinned to a subagent that had already finished. So a terminal event
 * that names nothing closes the oldest call of its shape still open, which is
 * the one it can only have ended.
 *
 * Two concurrent Codex calls whose terminal events both arrive receiver-less are
 * closed in the order they opened. Getting the pairing wrong there costs
 * nothing: the count is what the indicator shows, and it is the same either way.
 */
function closeSubagentCall(
  openCalls: OpenSubagentCall[],
  kind: WorkLogSubagentTask["kind"],
  keys: ReadonlyArray<string>,
): ReadonlyArray<string> {
  const matchedIndex =
    keys.length > 0
      ? openCalls.findIndex(
          (call) => call.kind === kind && call.keys.some((key) => keys.includes(key)),
        )
      : openCalls.findIndex((call) => call.kind === kind);
  const matched = matchedIndex >= 0 ? openCalls[matchedIndex] : undefined;
  if (matched) {
    openCalls.splice(matchedIndex, 1);
  }
  const closedKeys = new Set([...keys, ...(matched?.keys ?? [])]);
  // A key never belongs to two calls at once, but a stale duplicate would keep
  // the entry alive after its row is gone, so it is dropped everywhere.
  for (const call of openCalls) {
    call.keys = call.keys.filter((key) => !closedKeys.has(key));
  }
  return [...closedKeys];
}

function activeSubagentTaskEntries(
  subagentTask: WorkLogSubagentTask,
): ReadonlyArray<ActiveSubagentTask> {
  if (subagentTask.kind === "child-thread") {
    const receiversByProviderThreadId = new Map(
      (subagentTask.receivers ?? []).map((receiver) => [receiver.providerThreadId, receiver]),
    );
    const entries: ActiveSubagentTask[] = [];
    for (const providerThreadId of subagentTask.receiverProviderThreadIds ?? []) {
      const receiver = receiversByProviderThreadId.get(providerThreadId);
      const entry: ActiveSubagentTask = { key: `subagent-thread:${providerThreadId}` };
      if (receiver?.nickname) {
        entry.subagentType = receiver.nickname;
      }
      if (receiver?.role) {
        entry.description = receiver.role;
      }
      entries.push(entry);
    }
    return entries;
  }

  return [
    {
      key:
        subagentTask.taskToolUseId ??
        [
          subagentTask.subagentType ?? "",
          subagentTask.description ?? "",
          subagentTask.prompt ?? "",
        ].join("\u001f"),
      subagentType: subagentTask.subagentType,
      description: subagentTask.description,
    },
  ];
}

/**
 * What a delegated child thread has to look like to be judged. The read model
 * already drops deleted threads before they reach the store, so absence from
 * the collection is what "deleted" means here.
 *
 * `latestTurn` is only needed to tell a delegation that has *delivered* from one
 * that never ran (see `deriveDelegationStateByParent`); the live/idle split does
 * not use it.
 */
export interface DelegateThreadSnapshot {
  id: ThreadId;
  title?: string | null;
  parentThreadId?: ThreadId | null;
  subagentRole?: string | null;
  subagentNickname?: string | null;
  archivedAt?: string | null;
  session?: Pick<ThreadSession, "status" | "activeTurnId"> | null;
  latestTurn?: Pick<OrchestrationLatestTurn, "startedAt" | "completedAt"> | null;
  /** Read receipt left by the parent's `agent_delegate_poll`. */
  delegateReportReadAt?: string | null | undefined;
}

/**
 * What a thread's delegations amount to, as the sidebar and the chat need it.
 *
 * `liveCount` counts only *direct* delegations, which is what the thread itself
 * opened and can read — so it never contradicts the list the chat indicator
 * shows. `hasLiveDescendants` carries the same news up the chain without
 * inflating that number: with `MAX_DELEGATION_DEPTH = 2` a grandparent can be
 * waiting on work it did not open itself, and showing nothing there would leave
 * exactly the blind spot this state exists to close.
 *
 * `readyCount` is deliberately *not* propagated: a grandchild reporting back to
 * its own parent is that parent's business to read, not the grandparent's.
 */
export interface DelegationState {
  liveCount: number;
  hasLiveDescendants: boolean;
  /** Start of the longest-running direct delegation, for the chat's clock. */
  oldestLiveStartedAt: string | null;
  readyCount: number;
}

const EMPTY_DELEGATION_STATE: DelegationState = {
  liveCount: 0,
  hasLiveDescendants: false,
  oldestLiveStartedAt: null,
  readyCount: 0,
};

function earlierTimestamp(left: string | null, right: string | null | undefined): string | null {
  if (!right) return left;
  if (!left) return right;
  return Date.parse(right) < Date.parse(left) ? right : left;
}

/**
 * The delegation state of every thread that has delegations, in one pass.
 *
 * Built as a whole map rather than answered per parent because the sidebar asks
 * it for every row it draws, and scanning the thread collection once per row is
 * quadratic in the number of threads.
 */
export function deriveDelegationStateByParent(
  threads: ReadonlyArray<DelegateThreadSnapshot>,
): ReadonlyMap<ThreadId, DelegationState> {
  // Only the ancestor walk below needs this: readiness is now a property of the
  // delegation alone, so the parent no longer takes part in judging it.
  const threadById = new Map<ThreadId, DelegateThreadSnapshot>();
  for (const thread of threads) {
    threadById.set(thread.id, thread);
  }

  const childrenByParent = new Map<ThreadId, DelegateThreadSnapshot[]>();
  for (const thread of threads) {
    const parentThreadId = thread.parentThreadId;
    if (!parentThreadId || !isLiveDelegateThread(thread)) {
      continue;
    }
    const siblings = childrenByParent.get(parentThreadId);
    if (siblings) {
      siblings.push(thread);
    } else {
      childrenByParent.set(parentThreadId, [thread]);
    }
  }

  const stateByParent = new Map<ThreadId, DelegationState>();
  for (const [parentThreadId, children] of childrenByParent) {
    let liveCount = 0;
    let readyCount = 0;
    let oldestLiveStartedAt: string | null = null;
    for (const child of children) {
      if (isDelegateWorking(child)) {
        liveCount += 1;
        oldestLiveStartedAt = earlierTimestamp(oldestLiveStartedAt, child.latestTurn?.startedAt);
        continue;
      }
      if (hasUnreadDelegateReport(child)) {
        readyCount += 1;
      }
    }
    stateByParent.set(parentThreadId, {
      liveCount,
      hasLiveDescendants: liveCount > 0,
      oldestLiveStartedAt,
      readyCount,
    });
  }

  // Second pass for the chain: a live delegation makes every ancestor of its
  // parent a thread with work running underneath it. Walking up from each live
  // child costs one step per level (`MAX_DELEGATION_DEPTH` is 2) and the visited
  // set makes a cycle in malformed data terminate instead of hanging the UI.
  for (const children of childrenByParent.values()) {
    for (const child of children) {
      if (!isDelegateWorking(child)) continue;
      const visited = new Set<ThreadId>([child.id]);
      let ancestorId = child.parentThreadId;
      while (ancestorId && !visited.has(ancestorId)) {
        visited.add(ancestorId);
        const existing = stateByParent.get(ancestorId) ?? EMPTY_DELEGATION_STATE;
        if (!existing.hasLiveDescendants) {
          stateByParent.set(ancestorId, { ...existing, hasLiveDescendants: true });
        }
        ancestorId = threadById.get(ancestorId)?.parentThreadId ?? null;
      }
    }
  }

  return stateByParent;
}

/**
 * One row for a delegation, named the way the user will recognize it.
 *
 * Built in place rather than spread per entry: these run once per delegation on
 * every store update the sidebar and the chat react to.
 */
function describeDelegateRow(
  thread: DelegateThreadSnapshot,
  mode: "working" | "awaiting",
): ActiveSubagentTask {
  const nickname = thread.subagentNickname?.trim();
  const title = thread.title?.trim();
  const label = nickname && nickname.length > 0 ? nickname : title;
  const row: ActiveSubagentTask = {
    key: `delegate-thread:${thread.id}`,
    threadId: thread.id,
  };
  if (label) {
    row.subagentType = label;
    // The delegating agent titles the thread with what it asked for, so it is
    // worth showing next to the nickname — but never twice.
    if (title && title !== label) {
      row.description = title;
    }
  }
  if (mode === "awaiting") {
    row.awaitingParent = true;
  } else if (thread.latestTurn?.startedAt) {
    row.startedAt = thread.latestTurn.startedAt;
  }
  return row;
}

/**
 * The delegations of a managed agent that are working right now.
 *
 * Unlike the in-session shapes, a delegation is a real thread, so its state is
 * read from the thread itself instead of being reconstructed from the parent's
 * activities.
 *
 * These outlive the parent's turn by design — the parent delegates and keeps
 * going — so the caller must not hide them once its own turn settles.
 */
export function deriveDelegatedSubagentTasks(
  threads: ReadonlyArray<DelegateThreadSnapshot>,
  parentThreadId: ThreadId | null | undefined,
): ReadonlyArray<ActiveSubagentTask> {
  if (!parentThreadId) {
    return [];
  }

  return directDelegations(threads, parentThreadId)
    .filter((thread) => isDelegateWorking(thread))
    .map((thread) => describeDelegateRow(thread, "working"));
}

/**
 * The delegations that have answered and are waiting for their parent to read
 * them.
 *
 * Kept apart from the running ones rather than folded into the same list: a
 * delegation that already delivered must never be counted as work in flight, and
 * what the user can do about each is the opposite — wait, or nudge the thread.
 */
export function deriveDelegationsAwaitingParent(
  threads: ReadonlyArray<DelegateThreadSnapshot>,
  parentThreadId: ThreadId | null | undefined,
): ReadonlyArray<ActiveSubagentTask> {
  if (!parentThreadId) {
    return [];
  }

  return directDelegations(threads, parentThreadId)
    .filter((thread) => hasUnreadDelegateReport(thread))
    .map((thread) => describeDelegateRow(thread, "awaiting"));
}

/**
 * Joins the subagents read from the parent's activities with the delegations
 * read from its child threads. A key can only come from one of the two shapes,
 * but a duplicate would render twice, so the first entry wins.
 */
export function combineActiveSubagentTasks(
  ...groups: ReadonlyArray<ActiveSubagentTasksState | ReadonlyArray<ActiveSubagentTask> | null>
): ActiveSubagentTasksState | null {
  const tasks: ActiveSubagentTask[] = [];
  const seenKeys = new Set<string>();
  for (const group of groups) {
    if (!group) {
      continue;
    }
    for (const task of Array.isArray(group) ? group : (group as ActiveSubagentTasksState).tasks) {
      if (seenKeys.has(task.key)) {
        continue;
      }
      seenKeys.add(task.key);
      tasks.push(task);
    }
  }
  return tasks.length > 0 ? { tasks } : null;
}

// Keeps the UI "working" while the provider still has visible assistant text or
// background-task updates to finish for the latest turn.
export function hasLiveTurnTailWork(input: {
  latestTurn: Pick<OrchestrationLatestTurn, "turnId" | "completedAt"> | null;
  messages: ReadonlyArray<Pick<ChatMessage, "role" | "streaming" | "turnId">>;
  activities: ReadonlyArray<OrchestrationThreadActivity>;
  session?: Pick<ThreadSession, "orchestrationStatus"> | null;
}): boolean {
  const latestTurnId = input.latestTurn?.turnId;
  if (!latestTurnId) {
    return false;
  }

  const hasStreamingAssistantText = input.messages.some(
    (message) =>
      message.role === "assistant" && message.turnId === latestTurnId && message.streaming,
  );
  if (hasStreamingAssistantText) {
    // Once the turn is terminal, a stale `streaming` flag should not keep the
    // stop button/timer alive indefinitely.
    return input.latestTurn?.completedAt == null;
  }

  // Some providers can leave task lifecycle bookkeeping behind after the turn
  // has already closed. Once the session is no longer running, those stale
  // task rows should not keep the whole chat in a live state.
  if (input.session?.orchestrationStatus !== "running") {
    return false;
  }

  if (deriveActiveBackgroundTasksState(input.activities, latestTurnId) !== null) {
    return true;
  }

  return false;
}

export function findLatestProposedPlan(
  proposedPlans: ReadonlyArray<ProposedPlan>,
  latestTurnId: TurnId | string | null | undefined,
): LatestProposedPlanState | null {
  if (latestTurnId) {
    const matchingTurnPlan = [...proposedPlans]
      .filter((proposedPlan) => proposedPlan.turnId === latestTurnId)
      .toSorted(
        (left, right) =>
          left.updatedAt.localeCompare(right.updatedAt) || left.id.localeCompare(right.id),
      )
      .at(-1);
    if (matchingTurnPlan) {
      return toLatestProposedPlanState(matchingTurnPlan);
    }
  }

  const latestPlan = [...proposedPlans]
    .toSorted(
      (left, right) =>
        left.updatedAt.localeCompare(right.updatedAt) || left.id.localeCompare(right.id),
    )
    .at(-1);
  if (!latestPlan) {
    return null;
  }

  return toLatestProposedPlanState(latestPlan);
}

export function findSidebarProposedPlan(input: {
  threads: ReadonlyArray<Pick<Thread, "id" | "proposedPlans">>;
  latestTurn: Pick<OrchestrationLatestTurn, "turnId" | "sourceProposedPlan"> | null;
  latestTurnSettled: boolean;
  threadId: ThreadId | string | null | undefined;
}): LatestProposedPlanState | null {
  const activeThreadPlans =
    input.threads.find((thread) => thread.id === input.threadId)?.proposedPlans ?? [];

  if (!input.latestTurnSettled) {
    const sourceProposedPlan = input.latestTurn?.sourceProposedPlan;
    if (sourceProposedPlan) {
      const sourcePlan = input.threads
        .find((thread) => thread.id === sourceProposedPlan.threadId)
        ?.proposedPlans.find((plan) => plan.id === sourceProposedPlan.planId);
      if (sourcePlan) {
        return toLatestProposedPlanState(sourcePlan);
      }
    }
  }

  return findLatestProposedPlan(activeThreadPlans, input.latestTurn?.turnId ?? null);
}

export function hasActionableProposedPlan(
  proposedPlan: LatestProposedPlanState | Pick<ProposedPlan, "implementedAt"> | null,
): boolean {
  return proposedPlan !== null && proposedPlan.implementedAt === null;
}

export const CONTEXT_CLEAR_ACTIVITY_KIND = "context-clear";
export const CONTEXT_COMPACTION_ACTIVITY_KIND = "context-compaction";

/**
 * Boundary markers describe the conversation as a whole rather than a single
 * turn: `/clear` and a manually triggered `/compact` both happen *between*
 * turns, which is exactly why they carry no `turnId`.
 *
 * They are kept out of the per-turn filter so they stay anchored where they
 * happened — the timeline sorts by `createdAt`, so a boundary row sits at its
 * own moment in history and never moves into a later turn's block. What they
 * must never do is speak for the current turn: see `isWorkingLabelSource` in
 * `MessagesTimeline.logic.ts`, where a finished compaction used to keep
 * announcing itself as "Compacting conversation..." on every later message.
 *
 * A compaction that fires *during* a turn (Codex auto-compaction) carries that
 * turn's id, is not a boundary, and legitimately labels the working row.
 */
function isThreadBoundaryActivity(activity: OrchestrationThreadActivity): boolean {
  if (activity.turnId !== null) {
    return false;
  }
  return (
    activity.kind === CONTEXT_CLEAR_ACTIVITY_KIND ||
    activity.kind === CONTEXT_COMPACTION_ACTIVITY_KIND
  );
}

/**
 * Derives the transcript's work rows from every turn of the thread, not just the
 * latest one: messages are already rendered for the whole conversation, and
 * hiding older tool calls made the model's actual work invisible whenever a turn
 * ended under a turn id the client no longer tracked.
 *
 * The `/clear` cut is applied later, in `deriveTimelineEntries`.
 */
export function deriveWorkLogEntries(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): WorkLogEntry[] {
  const ordered = [...activities].toSorted(compareActivitiesByOrder);
  const userInputIndex = indexUserInputActivities(ordered);
  const entries = ordered
    .filter(
      (activity) =>
        activity.kind !== "tool.started" &&
        activity.kind !== "task.started" &&
        activity.kind !== "task.completed" &&
        activity.kind !== "account.rate-limits.updated" &&
        activity.kind !== "context-window.updated" &&
        activity.summary !== "Checkpoint captured" &&
        !isPlanBoundaryToolActivity(activity) &&
        !isMemoryToolActivity(activity) &&
        !isMergedUserInputResolution(activity, userInputIndex) &&
        !isUserInputToolActivity(activity) &&
        !isThinkingTokensRuntimeWarning(activity),
    )
    .map((activity) => toDerivedWorkLogEntry(activity, userInputIndex));
  return collapseDerivedWorkLogEntries(dropRedundantTaskProgressEntries(entries)).map(
    ({
      activityKind: _activityKind,
      collapseCommand: _collapseCommand,
      collapseKey: _collapseKey,
      toolName: _toolName,
      ...entry
    }) => entry,
  );
}

const RUNTIME_WARNING_ACTIVITY_KIND = "runtime.warning";

/**
 * The message a `runtime.warning` payload carries, e.g. "Reconnecting... 2/5".
 *
 * The server stores a fixed `"Runtime warning"` summary on every one of these
 * activities, so rendering the summary turns a row that had something to say
 * into a mute, endlessly repeated placeholder.
 */
function runtimeWarningMessage(activity: OrchestrationThreadActivity): string | undefined {
  if (activity.kind !== RUNTIME_WARNING_ACTIVITY_KIND) {
    return undefined;
  }
  const payload =
    activity.payload && typeof activity.payload === "object"
      ? (activity.payload as Record<string, unknown>)
      : null;
  const message = payload?.message;
  return typeof message === "string" && message.trim().length > 0 ? message.trim() : undefined;
}

/**
 * Hides the `thinking_tokens` warnings already persisted in older threads.
 *
 * The adapter no longer emits them, but orchestration events are immutable, so
 * the ones already recorded (93% of every runtime warning in production) can
 * only be dropped on the way to the screen.
 */
function isThinkingTokensRuntimeWarning(activity: OrchestrationThreadActivity): boolean {
  const message = runtimeWarningMessage(activity);
  return message !== undefined && message.includes("thinking_tokens");
}

function isPlanBoundaryToolActivity(activity: OrchestrationThreadActivity): boolean {
  if (activity.kind !== "tool.updated" && activity.kind !== "tool.completed") {
    return false;
  }

  const payload =
    activity.payload && typeof activity.payload === "object"
      ? (activity.payload as Record<string, unknown>)
      : null;
  return typeof payload?.detail === "string" && payload.detail.startsWith("ExitPlanMode:");
}

/**
 * The raw MCP row behind a `taskin_memory` call, which the domain row replaces.
 *
 * Every memory read otherwise lands in the transcript twice: once as
 * "Taskin memory read" — the tool's wire name, which says nothing about *which*
 * memory or why — and once as "Recordado: <título>". Two rows for one act is
 * noise, and the opaque one is the one a reader hits first.
 *
 * Dropped here rather than suppressed server-side because the tool call really
 * did happen and the event log should say so; this is a rendering decision, the
 * same shape as `isPlanBoundaryToolActivity` right above.
 */
function isMemoryToolActivity(activity: OrchestrationThreadActivity): boolean {
  if (
    activity.kind !== "tool.started" &&
    activity.kind !== "tool.updated" &&
    activity.kind !== "tool.completed"
  ) {
    return false;
  }
  const payload =
    activity.payload && typeof activity.payload === "object"
      ? (activity.payload as Record<string, unknown>)
      : null;
  const toolName = extractToolName(payload);
  // Claude mounts it as `mcp__taskin_memory__…`, the Codex proxy as
  // `taskin_memory__…`; matching the shared middle covers both without
  // pinning either provider's prefix convention.
  if (toolName === null || !toolName.includes("taskin_memory__")) return false;
  // A failed call is kept. There is no domain row standing in for it — the tool
  // only records `memory.recalled` once a slug actually resolves — so dropping
  // it would make "the agent asked for a memory that does not exist" vanish
  // from the transcript entirely, which is exactly the kind of thing someone
  // reading it later is looking for.
  return !extractToolResult(payload).isError;
}

/**
 * The thread's questions and their answers, keyed by request id.
 *
 * Built in one pass because the two halves of a question live in two separate
 * activities (`user-input.requested` carries the questions, `user-input.resolved`
 * the answers), and the row that shows them is derived from the first one.
 */
interface UserInputActivityIndex {
  questionsByRequestId: Map<string, ReadonlyArray<UserInputQuestion>>;
  answersByRequestId: Map<string, Record<string, unknown>>;
}

function activityRequestId(payload: Record<string, unknown> | null): string | null {
  return payload && typeof payload.requestId === "string" ? payload.requestId : null;
}

function indexUserInputActivities(
  ordered: ReadonlyArray<OrchestrationThreadActivity>,
): UserInputActivityIndex {
  const questionsByRequestId = new Map<string, ReadonlyArray<UserInputQuestion>>();
  const answersByRequestId = new Map<string, Record<string, unknown>>();

  for (const activity of ordered) {
    if (activity.kind !== "user-input.requested" && activity.kind !== "user-input.resolved") {
      continue;
    }
    const payload =
      activity.payload && typeof activity.payload === "object"
        ? (activity.payload as Record<string, unknown>)
        : null;
    const requestId = activityRequestId(payload);
    if (!requestId) {
      continue;
    }
    if (activity.kind === "user-input.requested") {
      const questions = parseUserInputQuestions(payload);
      if (questions) {
        questionsByRequestId.set(requestId, questions);
      }
      continue;
    }
    const answers = payload?.answers;
    answersByRequestId.set(
      requestId,
      answers && typeof answers === "object" && !Array.isArray(answers)
        ? (answers as Record<string, unknown>)
        : {},
    );
  }

  return { questionsByRequestId, answersByRequestId };
}

/**
 * Whether this `user-input.resolved` activity has already been folded into the
 * row of the question it answers.
 *
 * Only dropped when that row exists: history is paginated, so a page can hold
 * the answer without the question that provoked it. Dropping it there would
 * erase the decision instead of merging it, so the orphan keeps its own row.
 */
function isMergedUserInputResolution(
  activity: OrchestrationThreadActivity,
  index: UserInputActivityIndex,
): boolean {
  if (activity.kind !== "user-input.resolved") {
    return false;
  }
  const payload =
    activity.payload && typeof activity.payload === "object"
      ? (activity.payload as Record<string, unknown>)
      : null;
  const requestId = activityRequestId(payload);
  return requestId !== null && index.questionsByRequestId.has(requestId);
}

/**
 * The raw tool row behind a question, which the merged question row replaces.
 *
 * Claude runs `AskUserQuestion` as an ordinary tool once the user answers, so
 * without this the same interaction lands in the transcript twice: once as an
 * opaque "Ask user question" tool call, and once as the row that actually says
 * what was asked and what was decided. Same shape as `isMemoryToolActivity`:
 * the call really happened and the event log records it, this is a rendering
 * decision.
 */
function isUserInputToolActivity(activity: OrchestrationThreadActivity): boolean {
  if (
    activity.kind !== "tool.started" &&
    activity.kind !== "tool.updated" &&
    activity.kind !== "tool.completed"
  ) {
    return false;
  }
  const payload =
    activity.payload && typeof activity.payload === "object"
      ? (activity.payload as Record<string, unknown>)
      : null;
  const toolName = extractToolName(payload);
  return toolName === "AskUserQuestion";
}

/** The answer values submitted for one question, flattened to plain labels. */
function toAnswerValues(answer: unknown): string[] {
  if (typeof answer === "string") {
    return answer.trim().length > 0 ? [answer] : [];
  }
  if (Array.isArray(answer)) {
    return answer.filter((entry): entry is string => typeof entry === "string" && entry.length > 0);
  }
  return [];
}

function toWorkLogUserInputQuestion(
  question: UserInputQuestion,
  answer: unknown,
): WorkLogUserInputQuestion {
  const values = toAnswerValues(answer);
  const optionLabels = new Set(question.options.map((option) => option.label));
  const selectedLabels = values.filter((value) => optionLabels.has(value));
  // Anything that is not one of the offered options was typed by hand ("Other"),
  // and is the part of the answer a reader most needs to see verbatim.
  const custom = values.filter((value) => !optionLabels.has(value)).join(", ");
  return {
    id: question.id,
    header: question.header,
    question: question.question,
    options: question.options.map((option) => ({
      label: option.label,
      description: option.description,
    })),
    multiSelect: question.multiSelect ?? false,
    selectedLabels,
    ...(custom.length > 0 ? { customAnswer: custom } : {}),
  };
}

/**
 * The question rows built from an answer whose question is not on this page —
 * see [[isMergedUserInputResolution]]. The answer keys are the question ids, so
 * the row can still name what was decided even with the prompt itself missing.
 */
function toOrphanUserInputQuestions(
  answers: Record<string, unknown>,
): ReadonlyArray<WorkLogUserInputQuestion> {
  return Object.entries(answers).flatMap(([questionId, answer]) => {
    const values = toAnswerValues(answer);
    if (values.length === 0) {
      return [];
    }
    return [
      {
        id: questionId,
        header: questionId,
        question: questionId,
        options: [],
        multiSelect: values.length > 1,
        selectedLabels: values,
      } satisfies WorkLogUserInputQuestion,
    ];
  });
}

/** The question-and-answer block a `user-input.*` row shows, when it has one. */
function deriveWorkLogUserInput(
  activity: OrchestrationThreadActivity,
  payload: Record<string, unknown> | null,
  index: UserInputActivityIndex,
): WorkLogUserInput | null {
  if (activity.kind !== "user-input.requested" && activity.kind !== "user-input.resolved") {
    return null;
  }
  const requestId = activityRequestId(payload);
  if (!requestId) {
    return null;
  }
  const answers = index.answersByRequestId.get(requestId);
  const questions = index.questionsByRequestId.get(requestId);

  if (!questions) {
    // An answer whose question never made it onto this page.
    if (activity.kind !== "user-input.resolved" || !answers) {
      return null;
    }
    const orphanQuestions = toOrphanUserInputQuestions(answers);
    if (orphanQuestions.length === 0) {
      return null;
    }
    return {
      requestId: ApprovalRequestId.makeUnsafe(requestId),
      status: "answered",
      questions: orphanQuestions,
    };
  }

  const merged = questions.map((question) =>
    toWorkLogUserInputQuestion(question, answers?.[question.id]),
  );
  const anyAnswered = merged.some(
    (question) => question.selectedLabels.length > 0 || question.customAnswer !== undefined,
  );
  return {
    requestId: ApprovalRequestId.makeUnsafe(requestId),
    // An unanswered resolution is a retracted question: the turn was
    // interrupted while the agent was parked on it.
    status: answers === undefined ? "pending" : anyAnswered ? "answered" : "cancelled",
    questions: merged,
  };
}

function toDerivedWorkLogEntry(
  activity: OrchestrationThreadActivity,
  userInputIndex: UserInputActivityIndex = {
    questionsByRequestId: new Map(),
    answersByRequestId: new Map(),
  },
): DerivedWorkLogEntry {
  const payload =
    activity.payload && typeof activity.payload === "object"
      ? (activity.payload as Record<string, unknown>)
      : null;
  const command = extractToolCommand(payload);
  const changedFiles = extractChangedFiles(payload);
  const title = extractToolTitle(payload);
  const toolName = extractToolName(payload);
  const entry: DerivedWorkLogEntry = {
    id: activity.id,
    createdAt: activity.createdAt,
    label: runtimeWarningMessage(activity) ?? activity.summary,
    tone: activity.tone === "approval" ? "info" : activity.tone,
    activityKind: activity.kind,
    ...(activity.kind === "memory.recalled"
      ? { memoryKind: "recalled" as const }
      : activity.kind === "memory.proposed"
        ? { memoryKind: "proposed" as const }
        : {}),
    ...(isThreadBoundaryActivity(activity)
      ? {
          isBoundary: true,
          boundaryKind:
            activity.kind === CONTEXT_CLEAR_ACTIVITY_KIND
              ? ("clear" as const)
              : ("compaction" as const),
        }
      : {}),
    ...(toolName ? { toolName } : {}),
    ...(activity.turnId ? { turnId: activity.turnId } : {}),
  };
  const itemType = extractWorkLogItemType(payload);
  const requestKind = extractWorkLogRequestKind(payload);
  if (payload && typeof payload.detail === "string" && payload.detail.length > 0) {
    const detail = stripTrailingExitCode(payload.detail).output;
    if (detail) {
      entry.detail = detail;
    }
  }
  if (command) {
    entry.command = command;
  }
  const toolResult = extractToolResult(payload);
  if (toolResult.output !== undefined) {
    entry.output = toolResult.output;
  }
  if (toolResult.outputTruncated) {
    entry.outputTruncated = true;
  }
  if (toolResult.exitCode !== undefined) {
    entry.exitCode = toolResult.exitCode;
  }
  if (toolResult.isError) {
    entry.isError = true;
  }
  if (activity.kind === CONTEXT_COMPACTION_ACTIVITY_KIND) {
    const compaction = extractCompaction(payload);
    if (compaction) {
      entry.compaction = compaction;
    }
    if (entry.output === undefined) {
      const compactionOutput = extractCompactionOutput(payload);
      if (compactionOutput) {
        entry.output = compactionOutput;
      }
    }
  }
  if (changedFiles.length > 0) {
    entry.changedFiles = changedFiles;
  }
  if (itemType) {
    entry.itemType = itemType;
  }
  if (requestKind) {
    entry.requestKind = requestKind;
  }
  if (
    activity.kind === "user-input.requested" &&
    payload &&
    typeof payload.requestId === "string"
  ) {
    entry.userInputRequestId = ApprovalRequestId.makeUnsafe(payload.requestId);
  }
  const userInput = deriveWorkLogUserInput(activity, payload, userInputIndex);
  if (userInput) {
    entry.userInput = userInput;
  }
  const subagentTask = extractSubagentTask(activity, payload);
  if (subagentTask) {
    entry.subagentTask = subagentTask;
  }
  const readableTitle = deriveReadableToolTitle({
    title,
    fallbackLabel: activity.summary,
    itemType,
    requestKind,
    command,
    payload,
  });
  // The subagent's own name beats the generic tool title ("Agent task").
  const entryTitle = (subagentTask ? subagentTaskTitle(subagentTask) : null) ?? readableTitle;
  if (entryTitle) {
    entry.toolTitle = entryTitle;
  }
  const collapseKey = deriveToolLifecycleCollapseKey(entry);
  if (collapseKey) {
    entry.collapseKey = collapseKey;
  }
  const collapseCommand = deriveToolLifecycleCollapseCommand(entry);
  if (collapseCommand) {
    entry.collapseCommand = collapseCommand;
  }
  return entry;
}

/**
 * Drops the flat "Reasoning update" row a `task.progress` activity produces when
 * it only repeats the description of a subagent row that is already on screen.
 *
 * Claude reports a subagent's progress twice: once as the `Task` tool lifecycle
 * (now a row of its own, with its prompt and live action) and once as a task
 * heartbeat whose detail is literally the task description. A heartbeat that
 * carries a real `summary` says something new and is kept — this only removes
 * the duplicates.
 */
function dropRedundantTaskProgressEntries(
  entries: ReadonlyArray<DerivedWorkLogEntry>,
): DerivedWorkLogEntry[] {
  const subagentDescriptions = new Set<string>();
  for (const entry of entries) {
    const description = entry.subagentTask?.description;
    if (description) {
      subagentDescriptions.add(description);
    }
  }
  if (subagentDescriptions.size === 0) {
    return [...entries];
  }
  return entries.filter(
    (entry) =>
      entry.activityKind !== "task.progress" ||
      entry.detail === undefined ||
      !subagentDescriptions.has(entry.detail),
  );
}

function collapseDerivedWorkLogEntries(
  entries: ReadonlyArray<DerivedWorkLogEntry>,
): DerivedWorkLogEntry[] {
  const collapsed: DerivedWorkLogEntry[] = [];
  for (const entry of entries) {
    const previous = collapsed.at(-1);
    if (previous && shouldCollapseToolLifecycleEntries(previous, entry)) {
      collapsed[collapsed.length - 1] = mergeDerivedWorkLogEntries(previous, entry);
      continue;
    }
    if (previous && shouldCollapseRuntimeWarningEntries(previous, entry)) {
      collapsed[collapsed.length - 1] = {
        ...previous,
        count: (previous.count ?? 1) + 1,
      };
      continue;
    }
    collapsed.push(entry);
  }
  return collapsed;
}

/**
 * Folds a run of identical warnings into one counted row.
 *
 * A retry that reports "Reconnecting..." on every attempt is one event as far
 * as the reader is concerned; printing it once per attempt buries the rest of
 * the transcript. Only consecutive rows with the very same text merge, so two
 * different warnings never hide behind one another.
 */
function shouldCollapseRuntimeWarningEntries(
  previous: DerivedWorkLogEntry,
  next: DerivedWorkLogEntry,
): boolean {
  return (
    previous.activityKind === RUNTIME_WARNING_ACTIVITY_KIND &&
    next.activityKind === RUNTIME_WARNING_ACTIVITY_KIND &&
    previous.label === next.label &&
    previous.detail === next.detail
  );
}

function shouldCollapseToolLifecycleEntries(
  previous: DerivedWorkLogEntry,
  next: DerivedWorkLogEntry,
): boolean {
  if (previous.activityKind !== "tool.updated" && previous.activityKind !== "tool.completed") {
    return false;
  }
  if (next.activityKind !== "tool.updated" && next.activityKind !== "tool.completed") {
    return false;
  }
  if (previous.activityKind === "tool.completed") {
    return false;
  }
  // Rows from every turn share one list now, so the same tool called again in a
  // later turn must never be folded into the earlier turn's row.
  if (previous.turnId !== next.turnId) {
    return false;
  }
  if (previous.collapseKey === undefined || previous.collapseKey !== next.collapseKey) {
    return false;
  }
  return areToolLifecycleCommandsCompatible(previous.collapseCommand, next.collapseCommand);
}

function mergeDerivedWorkLogEntries(
  previous: DerivedWorkLogEntry,
  next: DerivedWorkLogEntry,
): DerivedWorkLogEntry {
  const changedFiles = mergeChangedFiles(previous.changedFiles, next.changedFiles);
  const detail = next.detail ?? previous.detail;
  const command = next.command ?? previous.command;
  // The tool result only lands on the last lifecycle event of the cycle, so keep it
  // when the earlier in-progress entries are collapsed into a single row.
  const toolResult = pickToolResult(previous, next);
  const toolTitle = next.toolTitle ?? previous.toolTitle;
  const itemType = next.itemType ?? previous.itemType;
  const requestKind = next.requestKind ?? previous.requestKind;
  const subagentTask = mergeSubagentTasks(previous.subagentTask, next.subagentTask);
  const collapseKey = next.collapseKey ?? previous.collapseKey;
  const toolName = next.toolName ?? previous.toolName;
  const {
    output: _previousOutput,
    outputTruncated: _previousOutputTruncated,
    exitCode: _previousExitCode,
    isError: _previousIsError,
    ...previousWithoutToolResult
  } = previous;
  const {
    output: _nextOutput,
    outputTruncated: _nextOutputTruncated,
    exitCode: _nextExitCode,
    isError: _nextIsError,
    ...nextWithoutToolResult
  } = next;
  return {
    ...previousWithoutToolResult,
    ...nextWithoutToolResult,
    ...toolResult,
    ...(detail ? { detail } : {}),
    ...(command ? { command } : {}),
    ...(changedFiles.length > 0 ? { changedFiles } : {}),
    ...(toolTitle ? { toolTitle } : {}),
    ...(itemType ? { itemType } : {}),
    ...(requestKind ? { requestKind } : {}),
    ...(subagentTask ? { subagentTask } : {}),
    ...(collapseKey ? { collapseKey } : {}),
    ...(toolName ? { toolName } : {}),
  };
}

/**
 * The lifecycle of one subagent call is spread across its events: the prompt and
 * the receivers arrive as the provider learns them, the live action only exists
 * while it runs, and the status is whatever the last event said. Taking `next`
 * wholesale would drop the receivers — and with them the "Open thread" links —
 * whenever the closing event does not repeat them.
 */
function mergeSubagentTasks(
  previous: WorkLogSubagentTask | undefined,
  next: WorkLogSubagentTask | undefined,
): WorkLogSubagentTask | undefined {
  if (!previous || !next) {
    return next ?? previous;
  }
  const receiverProviderThreadIds =
    next.receiverProviderThreadIds ?? previous.receiverProviderThreadIds;
  const receivers = next.receivers ?? previous.receivers;
  const threadLinks = next.threadLinks ?? previous.threadLinks;
  return {
    kind: next.kind,
    status: next.status,
    ...((next.subagentType ?? previous.subagentType)
      ? { subagentType: next.subagentType ?? previous.subagentType }
      : {}),
    ...((next.description ?? previous.description)
      ? { description: next.description ?? previous.description }
      : {}),
    ...((next.prompt ?? previous.prompt) ? { prompt: next.prompt ?? previous.prompt } : {}),
    ...((next.taskToolUseId ?? previous.taskToolUseId)
      ? { taskToolUseId: next.taskToolUseId ?? previous.taskToolUseId }
      : {}),
    // A finished row has no live action, so this one is never inherited.
    ...(next.currentAction ? { currentAction: next.currentAction } : {}),
    ...(receiverProviderThreadIds ? { receiverProviderThreadIds } : {}),
    ...(receivers ? { receivers } : {}),
    ...(threadLinks ? { threadLinks } : {}),
  };
}

type WorkLogToolResult = Pick<WorkLogEntry, "output" | "outputTruncated" | "exitCode" | "isError">;

function toolResultOf(entry: WorkLogEntry): WorkLogToolResult {
  return {
    ...(entry.output === undefined ? {} : { output: entry.output }),
    ...(entry.outputTruncated ? { outputTruncated: true } : {}),
    ...(entry.exitCode === undefined ? {} : { exitCode: entry.exitCode }),
    ...(entry.isError ? { isError: true } : {}),
  };
}

// The result of a tool call is reported once, on the last lifecycle event, so the
// collapsed row keeps whichever side actually carries it — as one coherent block,
// never mixing an output with the exit code of a different event.
function pickToolResult(previous: WorkLogEntry, next: WorkLogEntry): WorkLogToolResult {
  const nextResult = toolResultOf(next);
  return Object.keys(nextResult).length > 0 ? nextResult : toolResultOf(previous);
}

function mergeChangedFiles(
  previous: ReadonlyArray<string> | undefined,
  next: ReadonlyArray<string> | undefined,
): string[] {
  const merged = [...(previous ?? []), ...(next ?? [])];
  if (merged.length === 0) {
    return [];
  }
  return [...new Set(merged)];
}

// Keep a stable lifecycle key so providers like Claude can stream many
// in-progress tool deltas without turning each partial update into its own row.
function deriveToolLifecycleCollapseKey(entry: DerivedWorkLogEntry): string | undefined {
  if (entry.activityKind !== "tool.updated" && entry.activityKind !== "tool.completed") {
    return undefined;
  }
  if (entry.subagentTask) {
    return subagentTaskCollapseKey(entry.subagentTask);
  }
  const normalizedLabel = normalizeCompactToolLabel(entry.toolTitle ?? entry.label);
  const itemType = entry.itemType ?? "";
  const requestKind = entry.requestKind ?? "";
  const toolName = entry.toolName ?? "";
  const command = normalizeCompactToolLabel(entry.command ?? "");
  const changedFiles =
    entry.changedFiles && entry.changedFiles.length > 0 ? entry.changedFiles.join("|") : "";
  const detailHint = normalizeCompactToolLabel(extractDetailCollapseHint(entry.detail));
  if (
    normalizedLabel.length === 0 &&
    itemType.length === 0 &&
    requestKind.length === 0 &&
    toolName.length === 0 &&
    changedFiles.length === 0 &&
    detailHint.length === 0
  ) {
    return command.length > 0 ? `command-only${"\u001f"}${command}` : undefined;
  }
  return [itemType, normalizedLabel, requestKind, toolName, changedFiles, detailHint].join(
    "\u001f",
  );
}

/**
 * Subagent rows cannot be collapsed by their heading like other tools, because
 * that heading changes as the call progresses — it starts generic and only names
 * the agents once the provider has spawned them. Keying on it split one call into
 * an anonymous row plus a named one (the flapping seen in production).
 *
 * So each shape keys on what is stable from its very first update:
 *
 * - Codex: nothing about the receivers is known early, so the key is the shape
 *   itself — one row per collab call. A second call in the same turn still gets
 *   its own row, because a completed row never absorbs the next one.
 * - Claude: the `tool_use` id of the `Task` is known from the first update and
 *   never changes, so it is the key. Two concurrent subagents keep their own rows
 *   even when they share type, description and prompt — each one links to its own
 *   child thread, so merging them would hide one transcript. Activities recorded
 *   before the id was published fall back to the input content.
 */
function subagentTaskCollapseKey(subagentTask: WorkLogSubagentTask): string {
  if (subagentTask.kind === "child-thread") {
    return "subagent-task:child-thread";
  }
  if (subagentTask.taskToolUseId) {
    return `subagent-task:in-session:${subagentTask.taskToolUseId}`;
  }
  return [
    "subagent-task:in-session",
    subagentTask.subagentType ?? "",
    subagentTask.description ?? "",
    subagentTask.prompt ?? "",
  ].join("\u001f");
}

function deriveToolLifecycleCollapseCommand(entry: DerivedWorkLogEntry): string | undefined {
  const command = normalizeCompactToolLabel(entry.command ?? "");
  return command.length > 0 ? command : undefined;
}

function areToolLifecycleCommandsCompatible(
  previous: string | undefined,
  next: string | undefined,
): boolean {
  if (!previous || !next) {
    return true;
  }
  return previous === next || previous.startsWith(next) || next.startsWith(previous);
}

function toLatestProposedPlanState(proposedPlan: ProposedPlan): LatestProposedPlanState {
  return {
    id: proposedPlan.id,
    createdAt: proposedPlan.createdAt,
    updatedAt: proposedPlan.updatedAt,
    turnId: proposedPlan.turnId,
    planMarkdown: proposedPlan.planMarkdown,
    implementedAt: proposedPlan.implementedAt,
    implementationThreadId: proposedPlan.implementationThreadId,
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function asTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Reads the in-session subagent shape the server normalizes onto Claude `Task`
 * activities (`subagentType` / `description` / `prompt` / `currentAction`).
 *
 * The status comes from the lifecycle event rather than the payload: a
 * `tool.completed` activity carries no `status` field at all, and a `tool.updated`
 * one only carries it while something is still in flight.
 */
function extractSubagentTask(
  activity: OrchestrationThreadActivity,
  payload: Record<string, unknown> | null,
): WorkLogSubagentTask | undefined {
  if (extractWorkLogItemType(payload) !== "collab_agent_tool_call") {
    return undefined;
  }

  const data = asRecord(payload?.data);
  const failed = data?.isError === true || asTrimmedString(payload?.status) === "failed";
  const status: WorkLogSubagentTask["status"] = failed
    ? "failed"
    : activity.kind === "tool.completed"
      ? "completed"
      : "running";
  const prompt = asTrimmedString(data?.prompt);
  // A finished subagent has no current action, whatever the last update said.
  const currentAction = status === "running" ? asTrimmedString(data?.currentAction) : null;
  const childThread = extractChildThreadReceivers(data);

  if (childThread) {
    return {
      kind: "child-thread",
      status,
      ...(prompt ? { prompt } : {}),
      ...(currentAction ? { currentAction } : {}),
      ...childThread,
    };
  }

  const subagentType = asTrimmedString(data?.subagentType);
  const description = asTrimmedString(data?.description);
  const taskToolUseId = asTrimmedString(data?.taskToolUseId);
  return {
    kind: "in-session",
    status,
    ...(subagentType ? { subagentType } : {}),
    ...(description ? { description } : {}),
    ...(prompt ? { prompt } : {}),
    ...(currentAction ? { currentAction } : {}),
    ...(taskToolUseId ? { taskToolUseId } : {}),
  };
}

/**
 * Recognizes a child-thread subagent call (Codex) and reads its receivers.
 *
 * Three payload shapes reach this point:
 * - the bounded shape the server normalizes today (`receiverProviderThreadIds`);
 * - the raw app-server payload under `data.item`, which is what a `tool.updated`
 *   still falls back to before the provider has named any receiver, and what
 *   every activity persisted before that normalization existed looks like;
 * - Claude's in-session shape, which has neither — that returns `undefined`.
 *
 * Recognizing the receiver-less `data.item` shape matters: without it the early
 * updates of a Codex call would be classified as in-session and land on a second,
 * anonymous row instead of collapsing into the one that later names its agents.
 */
function extractChildThreadReceivers(data: Record<string, unknown> | null):
  | {
      receiverProviderThreadIds?: ReadonlyArray<string>;
      receivers?: ReadonlyArray<WorkLogSubagentTaskReceiver>;
    }
  | undefined {
  if (!data) {
    return undefined;
  }

  // The key existing at all is the signal, empty or not: a normalized payload
  // that lists no receiver yet is still a child-thread call, not a Claude one.
  const normalizedIds = Array.isArray(data.receiverProviderThreadIds)
    ? asTrimmedStringArray(data.receiverProviderThreadIds)
    : undefined;
  const rawItem = asRecord(data.item);
  if (normalizedIds === undefined && !rawItem) {
    return undefined;
  }

  const receiverProviderThreadIds =
    normalizedIds !== undefined
      ? normalizedIds
      : rawItem
        ? [...collectSubagentProviderThreadIds(rawItem)]
        : [];
  const receivers =
    readNormalizedReceivers(data.receiverAgents) ??
    (rawItem ? readRawReceivers(rawItem, receiverProviderThreadIds) : []);

  return {
    ...(receiverProviderThreadIds.length > 0 ? { receiverProviderThreadIds } : {}),
    ...(receivers.length > 0 ? { receivers } : {}),
  };
}

function asTrimmedStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((entry) => asTrimmedString(entry))
    .filter((entry): entry is string => entry !== null);
}

function readNormalizedReceivers(
  value: unknown,
): ReadonlyArray<WorkLogSubagentTaskReceiver> | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const receivers: WorkLogSubagentTaskReceiver[] = [];
  for (const entry of value) {
    const record = asRecord(entry);
    const providerThreadId = asTrimmedString(record?.providerThreadId);
    if (!providerThreadId) {
      continue;
    }
    const nickname = asTrimmedString(record?.nickname);
    const role = asTrimmedString(record?.role);
    const model = asTrimmedString(record?.model);
    const status = asTrimmedString(record?.status);
    receivers.push({
      providerThreadId,
      ...(nickname ? { nickname } : {}),
      ...(role ? { role } : {}),
      ...(model ? { model } : {}),
      ...(status ? { status } : {}),
    });
  }
  return receivers;
}

/** Names receivers straight out of a raw app-server collab payload. */
function readRawReceivers(
  rawItem: Record<string, unknown>,
  receiverProviderThreadIds: ReadonlyArray<string>,
): ReadonlyArray<WorkLogSubagentTaskReceiver> {
  const agentStates = decodeSubagentAgentStates(rawItem);
  const byProviderThreadId = new Map(
    decodeSubagentReceiverAgents(rawItem, receiverProviderThreadIds).map(
      (agent) => [agent.providerThreadId, agent] as const,
    ),
  );

  const receivers: WorkLogSubagentTaskReceiver[] = [];
  for (const providerThreadId of receiverProviderThreadIds) {
    const agent = byProviderThreadId.get(providerThreadId);
    const state = agentStates[providerThreadId];
    const nickname = asTrimmedString(state?.nickname ?? agent?.nickname);
    const role = asTrimmedString(state?.role ?? agent?.role);
    const model = asTrimmedString(state?.model ?? agent?.model);
    const rawStatus = asTrimmedString(state?.status);
    if (!nickname && !role && !model && !rawStatus) {
      continue;
    }
    receivers.push({
      providerThreadId,
      ...(nickname ? { nickname } : {}),
      ...(role ? { role } : {}),
      ...(model ? { model } : {}),
      ...(rawStatus ? { status: rawStatus } : {}),
    });
  }
  return receivers;
}

/**
 * "Subagent code-reviewer: Review the database layer" — the provider's own title
 * is the useless generic "Subagent task", so name the agent and its assignment
 * instead. Returns null when the payload identifies neither.
 */
function subagentTaskTitle(subagentTask: WorkLogSubagentTask): string | null {
  if (subagentTask.kind === "child-thread") {
    return childThreadSubagentTaskTitle(subagentTask);
  }
  const { subagentType, description } = subagentTask;
  if (subagentType && description) {
    return `Subagent ${subagentType}: ${description}`;
  }
  if (subagentType) {
    return `Subagent ${subagentType}`;
  }
  return description ? `Subagent task: ${description}` : null;
}

const MAX_NAMED_SUBAGENTS_IN_TITLE = 3;

/**
 * Codex names its subagents rather than describing the assignment, so the row
 * heading lists them: "Subagents Locke, Ada". Falls back to a count when the
 * provider gave no names, and to `null` before it has named any receiver at all —
 * at which point the generic "Agent task" title is the honest answer.
 */
function childThreadSubagentTaskTitle(subagentTask: WorkLogSubagentTask): string | null {
  const receiverProviderThreadIds = subagentTask.receiverProviderThreadIds ?? [];
  if (receiverProviderThreadIds.length === 0) {
    return null;
  }

  const nicknames = (subagentTask.receivers ?? [])
    .map((receiver) => receiver.nickname ?? receiver.role)
    .filter((name): name is string => Boolean(name));
  if (nicknames.length === 0) {
    return receiverProviderThreadIds.length === 1
      ? "Subagent task"
      : `Subagent task (${receiverProviderThreadIds.length})`;
  }

  const shown = nicknames.slice(0, MAX_NAMED_SUBAGENTS_IN_TITLE);
  const hidden = nicknames.length - shown.length;
  const names = hidden > 0 ? `${shown.join(", ")} +${hidden}` : shown.join(", ");
  return nicknames.length === 1 ? `Subagent ${names}` : `Subagents ${names}`;
}

function normalizeCommandValue(value: unknown): string | null {
  const direct = asTrimmedString(value);
  if (direct) {
    return direct;
  }
  if (!Array.isArray(value)) {
    return null;
  }
  const parts = value
    .map((entry) => asTrimmedString(entry))
    .filter((entry): entry is string => entry !== null);
  return parts.length > 0 ? parts.join(" ") : null;
}

function extractToolCommand(payload: Record<string, unknown> | null): string | null {
  const data = asRecord(payload?.data);
  const item = asRecord(data?.item);
  const itemResult = asRecord(item?.result);
  const itemInput = asRecord(item?.input);
  // `data.input.command` is the Claude shape (`{ toolName, input, result }`); the
  // nested `item` variants come from Codex, and `data.command` from the normalized
  // payload the server attaches to completed tool activities.
  const dataInput = asRecord(data?.input);
  const candidates = [
    normalizeCommandValue(item?.command),
    normalizeCommandValue(itemInput?.command),
    normalizeCommandValue(dataInput?.command),
    normalizeCommandValue(itemResult?.command),
    normalizeCommandValue(data?.command),
  ];
  return candidates.find((candidate) => candidate !== null) ?? null;
}

/**
 * Reads the bounded tool result the server normalizes onto `tool.completed`
 * activities. Older activities (persisted before that normalization existed) simply
 * carry none of these fields, so every consumer must tolerate their absence.
 */
function extractToolResult(payload: Record<string, unknown> | null): {
  output?: string;
  outputTruncated?: boolean;
  exitCode?: number;
  isError?: boolean;
} {
  const data = asRecord(payload?.data);
  if (!data) {
    return {};
  }
  const output = typeof data.output === "string" && data.output.length > 0 ? data.output : null;
  const exitCode =
    typeof data.exitCode === "number" && Number.isInteger(data.exitCode) ? data.exitCode : null;
  return {
    ...(output === null ? {} : { output }),
    ...(output !== null && data.outputTruncated === true ? { outputTruncated: true } : {}),
    ...(exitCode === null ? {} : { exitCode }),
    ...(data.isError === true ? { isError: true } : {}),
  };
}

function asTokenCount(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : undefined;
}

/**
 * Reads the compaction the server recorded on the activity payload.
 *
 * Nothing is synthesized: a field the provider never reported stays absent, and
 * the row shows less rather than something invented. `status` has a fallback
 * because it is what every consumer keys off, and a payload that reached this
 * function is a compaction one way or another; a compaction reported without a
 * status is one nobody told us had finished, so it reads as in-flight.
 */
function extractCompaction(payload: Record<string, unknown> | null): WorkLogCompaction | undefined {
  if (!payload) {
    return undefined;
  }
  const status =
    payload.status === "completed" || payload.status === "failed" ? payload.status : "inProgress";
  const trigger =
    payload.trigger === "manual" || payload.trigger === "auto" ? payload.trigger : undefined;
  const preTokens = asTokenCount(payload.preTokens);
  const postTokens = asTokenCount(payload.postTokens);
  const summaryText = asTrimmedString(payload.summaryText);
  return {
    status,
    ...(trigger ? { trigger } : {}),
    ...(preTokens !== undefined ? { preTokens } : {}),
    ...(postTokens !== undefined ? { postTokens } : {}),
    ...(summaryText ? { summaryText } : {}),
  };
}

/**
 * The text a compaction row expands into, the same way a tool row expands into
 * its command output.
 *
 * The recap the agent kept is the whole point of opening the row, so it wins
 * over the status line. Threads compacted before the payload was normalized
 * still carry Claude's raw `data.compact_metadata`, which is serialized as a
 * last resort so their rows do not go blank. A compaction with none of the
 * three yields `undefined` and the row stays non-expandable.
 */
function extractCompactionOutput(payload: Record<string, unknown> | null): string | undefined {
  if (!payload) {
    return undefined;
  }
  const summaryText = asTrimmedString(payload.summaryText);
  if (summaryText) {
    return summaryText;
  }
  const detail = asTrimmedString(payload.detail);
  if (detail) {
    return detail;
  }
  const legacyMetadata = asRecord(payload.data)?.compact_metadata;
  if (legacyMetadata !== undefined && legacyMetadata !== null) {
    const serialized =
      typeof legacyMetadata === "string" ? legacyMetadata : JSON.stringify(legacyMetadata, null, 2);
    const trimmed = serialized.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  return undefined;
}

function extractToolTitle(payload: Record<string, unknown> | null): string | null {
  return asTrimmedString(payload?.title);
}

function extractToolName(payload: Record<string, unknown> | null): string | null {
  const data = asRecord(payload?.data);
  const item = asRecord(data?.item);
  const itemInput = asRecord(item?.input);
  const candidates = [data?.toolName, item?.toolName, item?.name, itemInput?.toolName];
  for (const candidate of candidates) {
    const normalized = asTrimmedString(candidate);
    if (normalized) {
      return normalized;
    }
  }
  return null;
}

function stripTrailingExitCode(value: string): {
  output: string | null;
  exitCode?: number | undefined;
} {
  const trimmed = value.trim();
  const match = /^(?<output>[\s\S]*?)(?:\s*<exited with exit code (?<code>\d+)>)\s*$/i.exec(
    trimmed,
  );
  if (!match?.groups) {
    return {
      output: trimmed.length > 0 ? trimmed : null,
    };
  }
  const exitCode = Number.parseInt(match.groups.code ?? "", 10);
  const normalizedOutput = match.groups.output?.trim() ?? "";
  return {
    output: normalizedOutput.length > 0 ? normalizedOutput : null,
    ...(Number.isInteger(exitCode) ? { exitCode } : {}),
  };
}

function extractDetailCollapseHint(detail: string | undefined): string {
  if (!detail) {
    return "";
  }
  const firstLine = detail.split("\n", 1)[0]?.trim() ?? "";
  if (firstLine.length === 0) {
    return "";
  }
  const colonIndex = firstLine.indexOf(":");
  if (colonIndex <= 0) {
    return firstLine;
  }
  return firstLine.slice(0, colonIndex);
}

function extractWorkLogItemType(
  payload: Record<string, unknown> | null,
): WorkLogEntry["itemType"] | undefined {
  if (typeof payload?.itemType === "string" && isToolLifecycleItemType(payload.itemType)) {
    return payload.itemType;
  }
  return undefined;
}

function extractWorkLogRequestKind(
  payload: Record<string, unknown> | null,
): WorkLogEntry["requestKind"] | undefined {
  if (
    payload?.requestKind === "command" ||
    payload?.requestKind === "file-read" ||
    payload?.requestKind === "file-change"
  ) {
    return payload.requestKind;
  }
  return requestKindFromRequestType(payload?.requestType) ?? undefined;
}

function pushChangedFile(target: string[], seen: Set<string>, value: unknown) {
  const normalized = asTrimmedString(value);
  if (!normalized || seen.has(normalized)) {
    return;
  }
  seen.add(normalized);
  target.push(normalized);
}

function collectChangedFiles(value: unknown, target: string[], seen: Set<string>, depth: number) {
  if (depth > 4 || target.length >= 12) {
    return;
  }
  if (Array.isArray(value)) {
    for (const entry of value) {
      collectChangedFiles(entry, target, seen, depth + 1);
      if (target.length >= 12) {
        return;
      }
    }
    return;
  }

  const record = asRecord(value);
  if (!record) {
    return;
  }

  pushChangedFile(target, seen, record.path);
  pushChangedFile(target, seen, record.filePath);
  pushChangedFile(target, seen, record.relativePath);
  pushChangedFile(target, seen, record.filename);
  pushChangedFile(target, seen, record.newPath);
  pushChangedFile(target, seen, record.oldPath);

  for (const nestedKey of [
    "item",
    "result",
    "input",
    "data",
    "changes",
    "files",
    "edits",
    "patch",
    "patches",
    "operations",
  ]) {
    if (!(nestedKey in record)) {
      continue;
    }
    collectChangedFiles(record[nestedKey], target, seen, depth + 1);
    if (target.length >= 12) {
      return;
    }
  }
}

function extractChangedFiles(payload: Record<string, unknown> | null): string[] {
  const changedFiles: string[] = [];
  const seen = new Set<string>();
  collectChangedFiles(asRecord(payload?.data), changedFiles, seen, 0);
  return changedFiles;
}

function compareActivitiesByOrder(
  left: OrchestrationThreadActivity,
  right: OrchestrationThreadActivity,
): number {
  if (left.sequence !== undefined && right.sequence !== undefined) {
    if (left.sequence !== right.sequence) {
      return left.sequence - right.sequence;
    }
  } else if (left.sequence !== undefined) {
    return 1;
  } else if (right.sequence !== undefined) {
    return -1;
  }

  const createdAtComparison = left.createdAt.localeCompare(right.createdAt);
  if (createdAtComparison !== 0) {
    return createdAtComparison;
  }

  const lifecycleRankComparison =
    compareActivityLifecycleRank(left.kind) - compareActivityLifecycleRank(right.kind);
  if (lifecycleRankComparison !== 0) {
    return lifecycleRankComparison;
  }

  return left.id.localeCompare(right.id);
}

function compareActivityLifecycleRank(kind: string): number {
  if (kind.endsWith(".started") || kind === "tool.started") {
    return 0;
  }
  if (kind.endsWith(".progress") || kind.endsWith(".updated")) {
    return 1;
  }
  if (kind.endsWith(".completed") || kind.endsWith(".resolved")) {
    return 2;
  }
  return 1;
}

export function hasToolActivityForTurn(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
  turnId: TurnId | null | undefined,
): boolean {
  if (!turnId) return false;
  return activities.some((activity) => activity.turnId === turnId && activity.tone === "tool");
}

/**
 * When the conversation was last replaced by a compaction.
 *
 * A completed compaction is the moment the agent stopped being able to see what
 * came before, so the transcript stops showing it too — the rows above are no
 * longer part of the conversation the user is having. Deliberately keyed off
 * the row's position and status, not off `isBoundary`: a compaction that fires
 * inside a turn carries that turn's id and is not a boundary row, but it
 * replaced the context just the same.
 *
 * Only `completed` counts. An in-flight compaction has not replaced anything
 * yet, and a failed one left the conversation intact — hiding history for
 * either would delete a transcript the agent can still read.
 */
function findLatestCompactionCutOff(workEntries: WorkLogEntry[]): string | null {
  let cutOff: string | null = null;
  for (const entry of workEntries) {
    if (entry.compaction?.status !== "completed") {
      continue;
    }
    if (cutOff === null || entry.createdAt > cutOff) {
      cutOff = entry.createdAt;
    }
  }
  return cutOff;
}

/**
 * Single place the conversation cut-off is applied.
 *
 * Everything the conversation view renders — messages, proposed plans and work
 * rows — funnels through here, so one comparison hides the whole history
 * instead of the same rule being restated for each source. Nothing is deleted:
 * the rows still exist in the store, they are simply never rendered again, and
 * no count or affordance is exposed that would let the user get them back.
 *
 * Two things cut: `/clear` and a completed compaction. They are the same kind
 * of event — the context stopped containing what came before — so the most
 * recent of the two wins rather than either overriding the other.
 *
 * The comparison is inclusive so the marker itself — the clear row, or the
 * compaction row stamped with the instant it finished — survives as the first
 * row after the cut.
 */
export function deriveTimelineEntries(
  messages: ChatMessage[],
  proposedPlans: ProposedPlan[],
  workEntries: WorkLogEntry[],
  contextClearedAt?: string | null,
): TimelineEntry[] {
  const messageRows: TimelineEntry[] = messages.map((message) => ({
    id: message.id,
    kind: "message",
    createdAt: message.createdAt,
    message,
  }));
  const proposedPlanRows: TimelineEntry[] = proposedPlans.map((proposedPlan) => ({
    id: proposedPlan.id,
    kind: "proposed-plan",
    createdAt: proposedPlan.createdAt,
    proposedPlan,
  }));
  const workRows: TimelineEntry[] = workEntries.map((entry) => ({
    id: entry.id,
    kind: "work",
    createdAt: entry.createdAt,
    entry,
  }));
  // Plain lexicographic comparison, not localeCompare: these are all ISO-8601 UTC
  // timestamps of the same format, so `<`/`>` orders them identically while
  // skipping locale-aware collation, which is meaningfully more expensive per
  // comparison on a timeline that can hold thousands of rows.
  const rows = [...messageRows, ...proposedPlanRows, ...workRows].toSorted((a, b) =>
    a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0,
  );
  const compactedAt = findLatestCompactionCutOff(workEntries);
  const cutOff =
    contextClearedAt && compactedAt
      ? contextClearedAt > compactedAt
        ? contextClearedAt
        : compactedAt
      : (contextClearedAt ?? compactedAt);
  if (!cutOff) {
    return rows;
  }
  return rows.filter((row) => row.createdAt >= cutOff);
}

export function inferCheckpointTurnCountByTurnId(
  summaries: TurnDiffSummary[],
): Record<TurnId, number> {
  const sorted = [...summaries].toSorted((a, b) => a.completedAt.localeCompare(b.completedAt));
  const result: Record<TurnId, number> = {};
  for (let index = 0; index < sorted.length; index += 1) {
    const summary = sorted[index];
    if (!summary) continue;
    result[summary.turnId] = index + 1;
  }
  return result;
}

export function derivePhase(session: ThreadSession | null): SessionPhase {
  if (!session || session.status === "closed") return "disconnected";
  if (session.status === "connecting") return "connecting";
  if (session.status === "running") return "running";
  return "ready";
}
